import React, { useState, useEffect } from 'react';
import {
    fetchAllCarriers,
    fetchThematicsByCarrierIdCall,
    fetchSubSubthematic
} from "app/Api Calls/CarriersThematicsCalls";
import CategoriesDropdown from "../Reusable Components/CategoriesDropdown";
import Button from "@mui/material/Button";
import {
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    Switch
} from "@mui/material";
import { EditorState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from "draftjs-to-html";
import {
    createCarrier,
    createQuestionNAnswer,
    createThematic
} from "app/Api Calls/GnosiakiCalls";
import { triggerGlobalRefresh } from "store/gnosiakiCrudSlice";
import { useDispatch } from "react-redux";
import DOMPurify from "dompurify";
import {showMessage} from "store/messageSlice";

const TreeComponent = () => {
    // Hierarchy for dropdowns
    const [hierarchy, setHierarchy] = useState([{ items: [], selectedId: null }]);
    // Text input fields
    const [newItemName, setNewItemName] = useState('');
    const [question, setQuestion] = useState('');
    const [customId, setCustomId] = useState('');

    // Editor state for QnA answer
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

    // New switches state for adding items (for carriers/thematics)
    const [newItemIsActive, setNewItemIsActive] = useState(true);
    const [newItemIsPublished, setNewItemIsPublished] = useState(true);

    // New switches state for Q&A entries
    const [qnaIsActive, setQnaIsActive] = useState(true);
    const [qnaIsPublished, setQnaIsPublished] = useState(true);

    const dispatch = useDispatch();

    // Show Q&A section when hierarchy is deep enough and no further options exist.
    const showQnASection = hierarchy.length > 3 && hierarchy[hierarchy.length - 1].items.length === 0;

    const fetchData = async (level, parentId = null) => {
        let data;
        if (level === 0) {
            data = await fetchAllCarriers();
        } else if (level === 1) {
            data = await fetchThematicsByCarrierIdCall(parentId);
        } else {
            data = await fetchSubSubthematic(parentId);
        }
        return data.map(item => ({ value: item.id, label: item.title }));
    };

    const handleSelect = (level, selectedOption) => {
        const newHierarchy = hierarchy.slice(0, level + 1);
        const selectedId = selectedOption ? selectedOption.value : null;
        if (selectedId) {
            fetchData(level + 1, selectedId).then(data => {
                newHierarchy[level] = { ...newHierarchy[level], selectedId };
                newHierarchy.push({ items: data, selectedId: null });
                setHierarchy(newHierarchy);
            });
        } else {
            newHierarchy[level] = { items: newHierarchy[level].items, selectedId: null };
            setHierarchy(newHierarchy);
        }
    };

    const handleAddNewItem = async (level) => {
        try {
            let response;
            // For thematic levels beyond carriers, determine parentId and carrierId accordingly.
            const parentId = level === 1 ? null : hierarchy[level - 2]?.selectedId;
            const carrierId = hierarchy[0]?.selectedId;
            if (level === 1) {
                // Adding a carrier: only pass isActive and customId.
                response = await createCarrier({
                    title: newItemName,
                    isActive: newItemIsActive,
                    customId: customId,
                });
                dispatch(triggerGlobalRefresh({ carrierId: response.data.id }));
                dispatch(showMessage({
                    message: 'Ο φορέας προστέθηκε επιτυχώς',
                    autoHideDuration: 2000,
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    variant: 'success',
                }));
            } else {
                // Adding a thematic: include both isActive and isPublished.
                response = await createThematic({
                    title: newItemName,
                    isActive: newItemIsActive,
                    externalId: null,
                    parentId: level === 2 ? null : parentId,
                    carrierId: carrierId,
                    isPublished: newItemIsPublished,
                });
                dispatch(triggerGlobalRefresh({ parentId }));
                dispatch(showMessage({
                    message: 'Η θεματική προστέθηκε επιτυχώς',
                    autoHideDuration: 2000,
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    variant: 'success',
                }));
            }
            // Reset new item fields and switches
            setNewItemName('');
            setCustomId('');
            setNewItemIsActive(true);
            setNewItemIsPublished(true);
            await refetchCurrentLevel(level, parentId);
        } catch (error) {
            dispatch(showMessage({
                message: 'Σφάλμα κατά την προσθήκη — δοκιμάστε ξανά',
                autoHideDuration: 2000,
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
                variant: 'error',
            }));
            console.error("Failed to add new item:", error);
        }
    };

    const refetchCurrentLevel = async (level, parentId) => {
        let newData;
        if (level === 1) {
            newData = await fetchAllCarriers();
        } else if (level === 2) {
            newData = await fetchThematicsByCarrierIdCall(parentId);
        } else {
            newData = await fetchSubSubthematic(parentId);
        }
        const newLevelData = {
            items: newData.map(dataItem => ({
                value: dataItem.id,
                label: dataItem.title || dataItem.name
            })),
            selectedId: null
        };
        const adjustedLevel = level - 1;
        let updatedHierarchy;
        if (adjustedLevel < hierarchy.length) {
            updatedHierarchy = hierarchy.map((item, index) => index === adjustedLevel ? newLevelData : item);
        } else {
            updatedHierarchy = [...hierarchy, newLevelData];
        }
        setHierarchy(updatedHierarchy);
    };

    const handleAddQnA = async () => {
        try {
            let answerHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
            answerHtml = DOMPurify.sanitize(answerHtml);
            answerHtml = answerHtml.replace(/\n+$/, '');
            if (answerHtml === "") return;

            if (answerHtml.length > 65535) {
                dispatch(showMessage({
                    message: 'Η απάντηση υπερβαίνει τους 65.535 χαρακτήρες. Παρακαλώ μειώστε το κείμενο ή τις εικόνες.',
                    autoHideDuration: 5000,
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    variant: 'error',
                }));
                return;
            }

            const thematicId = hierarchy[hierarchy.length - 2]?.selectedId;
            const response = await createQuestionNAnswer({
                title: question,
                question,
                answer: answerHtml,
                isActive: qnaIsActive ? 1 : 0,
                thematicId,
                isPublished: qnaIsPublished ? 1 : 0
            });
            console.log(response);
            setQuestion('');
            setEditorState(EditorState.createEmpty());
            setQnaIsActive(true);
            setQnaIsPublished(true);
            dispatch(triggerGlobalRefresh({ parentId: thematicId }));
            dispatch(showMessage({
                message: 'H Ερώτηση-απάντηση αποθηκεύτηκε επιτυχώς',
                autoHideDuration: 2000,
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
                variant: 'success',
            }));
        } catch (error) {
            dispatch(showMessage({
                message: 'Σφάλμα κατά την αποθήκευση της ερώτησης-απάντησης',
                autoHideDuration: 2000,
                anchorOrigin: { vertical: 'top', horizontal: 'center' },
                variant: 'error',
            }));
            console.error("Failed to add QnA:", error);
        }
    };

    useEffect(() => {
        fetchData(0).then(data => {
            setHierarchy([{ items: data, selectedId: null }]);
        });
    }, []);

    const removeHtmlTags = (htmlString) => {
        const tmp = document.createElement("DIV");
        tmp.innerHTML = htmlString;
        return tmp.textContent || tmp.innerText || "";
    };

    const imageUploadCallback = (file) =>
        new Promise((resolve, reject) => {
            const currentHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
            const currentLength = currentHtml.length;
            const imgTagOverhead = 100;
            const estimatedBase64Length = Math.ceil(file.size * 4 / 3);
            const totalEstimated = currentLength + imgTagOverhead + estimatedBase64Length;
            const MAX_ANSWER_LENGTH = 65535;

            if (totalEstimated > MAX_ANSWER_LENGTH) {
                dispatch(showMessage({
                    message: 'Η εικόνα είναι πολύ μεγάλη. Το συνολικό μέγεθος της απάντησης δεν μπορεί να υπερβαίνει τους 65.535 χαρακτήρες. Παρακαλώ επιλέξτε μικρότερη εικόνα.',
                    autoHideDuration: 5000,
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    variant: 'error',
                }));
                reject(new Error('Image exceeds size limit'));
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => resolve({ data: { link: e.target.result } });
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    return (
        <>
            <style>{`
                .rdw-editor-main img {
                    max-width: 100% !important;
                    height: auto !important;
                }
                .rdw-editor-toolbar {
                    flex-wrap: wrap !important;
                }
                .rdw-option-wrapper, .rdw-dropdown-wrapper {
                    min-width: auto !important;
                }
                .rdw-image-modal {
                    position: fixed !important;
                    top: 50% !important;
                    left: 50% !important;
                    transform: translate(-50%, -50%) !important;
                    right: auto !important;
                    bottom: auto !important;
                    max-width: 90vw !important;
                    max-height: 90vh !important;
                    overflow-y: auto !important;
                    z-index: 9999 !important;
                }
                @media (max-width: 600px) {
                    .rdw-image-modal {
                        max-width: 95vw !important;
                    }
                    .rdw-image-modal-header {
                        flex-direction: column !important;
                        align-items: stretch !important;
                    }
                    .rdw-image-modal-header-label {
                        margin: 2px 0 !important;
                    }
                }
            `}</style>
        <Card variant="outlined" sx={{ width: '100%', maxWidth: '1400px', mx: 'auto', mt: { xs: 1, sm: 2, md: 4 } }}>
            <CardContent sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                <Typography variant="h5" component="div" align="center" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
                    Προσθήκη Φορέων / Θεματικών / Ερωτοαπαντήσεων
                </Typography>
                {hierarchy.map((levelData, index) => (
                    <Grid
                        container
                        key={index}
                        spacing={{ xs: 1, sm: 2 }}
                        alignItems="center"
                        sx={{ mb: 1 }}
                    >
                        <Grid item xs={12} sm={12} md={4} lg={3}>
                            <CategoriesDropdown
                                options={levelData.items}
                                value={levelData.items.find(
                                    (item) => item.value === levelData.selectedId
                                )}
                                onChange={(selectedOption) =>
                                    handleSelect(index, selectedOption)
                                }
                                placeholder={
                                    index === 0
                                        ? "Επιλέξτε Φορέα"
                                        : index === 1
                                            ? "Εισάγετε Θεματική Ενότητα"
                                            : index === 2
                                                ? "Εισάγετε Θέμα"
                                                : index === 3
                                                    ? "Εισάγετε Υποθέμα"
                                                    : "No options available"
                                }
                                isLoading={false}
                                backgroundColor="#fff"
                                isMultiSelect={false}
                                isDisabled={false}
                            />
                        </Grid>
                        {(index === hierarchy.length - 1 && hierarchy.length <= 4) && (
                            <Grid item xs={12} sm={12} md={8} lg={9}>
                                <Grid
                                    container
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <Grid item xs={12} sm={6} md={5} lg={4}>
                                        <TextField
                                            label="Εισάγετε κείμενο"
                                            value={newItemName}
                                            onChange={(e) => setNewItemName(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                        />
                                    </Grid>
                                    {index === 0 && (
                                        <Grid item xs={12} sm={6} md={3} lg={2}>
                                            <TextField
                                                label="Custom ID"
                                                value={customId}
                                                onChange={(e) => setCustomId(e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6} md={4} lg={3}>
                                        {index === 0 ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Switch
                                                    checked={newItemIsActive}
                                                    onChange={(e) => setNewItemIsActive(e.target.checked)}
                                                    color="primary"
                                                    size="small"
                                                />
                                                <Typography variant="body2">Is Active</Typography>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Switch
                                                        checked={newItemIsActive}
                                                        onChange={(e) => setNewItemIsActive(e.target.checked)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                    <Typography variant="body2">Active</Typography>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Switch
                                                        checked={newItemIsPublished}
                                                        onChange={(e) => setNewItemIsPublished(e.target.checked)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                    <Typography variant="body2">Published</Typography>
                                                </div>
                                            </div>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3} lg={2}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            disabled={
                                                newItemName.length < 3 ||
                                                (index === 0 && customId.trim().length === 0)
                                            }
                                            onClick={() => handleAddNewItem(index + 1)}
                                            sx={{
                                                backgroundColor:
                                                    newItemName.length < 3 ||
                                                    (index === 0 && customId.trim().length === 0)
                                                        ? '#d3d3d3 !important'
                                                        : '#003476 !important',
                                                '&:hover': {
                                                    backgroundColor:
                                                        newItemName.length < 3 ||
                                                        (index === 0 && customId.trim().length === 0)
                                                            ? '#d3d3d3'
                                                            : '#0056b3',
                                                },
                                                color: (theme) =>
                                                    theme.palette.getContrastText(
                                                        newItemName.length < 3 ||
                                                        (index === 0 && customId.trim().length === 0)
                                                            ? '#d3d3d3'
                                                            : '#003476'
                                                    ),
                                            }}
                                        >
                                            ΠΡΟΣΘΗΚΗ
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                ))}
                {showQnASection && (
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Ερώτηση"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                variant="outlined"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body1">Απάντηση</Typography>
                            <div style={{ border: '1px solid #F1F1F1', borderRadius: '4px', overflow: 'hidden' }}>
                                <Editor
                                    editorState={editorState}
                                    onEditorStateChange={setEditorState}
                                    placeholder="Προσθέστε Απάντηση"
                                    toolbar={{
                                        options: [
                                            'inline',
                                            'blockType',
                                            'fontSize',
                                            'fontFamily',
                                            'list',
                                            'textAlign',
                                            'history',
                                            'image'
                                        ],
                                        inline: {
                                            options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                                        },
                                        blockType: { dropdownClassName: 'dropdown-classname' },
                                        fontSize: {},
                                        fontFamily: {
                                            options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana'],
                                            dropdownClassName: 'dropdown-classname'
                                        },
                                        list: { options: ['unordered', 'ordered', 'indent', 'outdent'] },
                                        textAlign: { options: ['left', 'center', 'right', 'justify'] },
                                        history: { options: ['undo', 'redo'] },
                                        image: {
                                            uploadCallback: imageUploadCallback,
                                            alt: { present: false, mandatory: false },
                                            previewImage: true,
                                            inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg,image/webp',
                                            defaultSize: {
                                                height: 'auto',
                                                width: 'auto',
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4} md={3}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Switch
                                            checked={qnaIsActive}
                                            onChange={(e) => setQnaIsActive(e.target.checked)}
                                            color="primary"
                                            size="small"
                                        />
                                        <Typography variant="body2">Is Active</Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Switch
                                            checked={qnaIsPublished}
                                            onChange={(e) => setQnaIsPublished(e.target.checked)}
                                            color="primary"
                                            size="small"
                                        />
                                        <Typography variant="body2">Is Published</Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={4} md={3}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleAddQnA}
                                        sx={{
                                            backgroundColor: (theme) =>
                                                question.length < 10 ||
                                                removeHtmlTags(
                                                    draftToHtml(convertToRaw(editorState.getCurrentContent()))
                                                ).length < 10
                                                    ? '#d3d3d3 !important'
                                                    : '#003476 !important',
                                            color: (theme) =>
                                                question.length < 10 ||
                                                removeHtmlTags(
                                                    draftToHtml(convertToRaw(editorState.getCurrentContent()))
                                                ).length < 10
                                                    ? theme.palette.getContrastText('#d3d3d3')
                                                    : theme.palette.getContrastText('#003476'),
                                        }}
                                    >
                                        Προσθήκη Ερώτησης
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </CardContent>
        </Card>
        </>
    );
};

export default TreeComponent;
